//
// ClassData
//
// A plain ClassData object to hold properties

import { action, computed, makeObservable, observable } from 'mobx'

import { test_empty } from '../../utils/utils'
import { clearMetadataField, setMetadataField, updateInstanceWithData } from './utils'

export class ClassData {
    gid = null
    original = null
    name = ''
    label = new Map()
    fields = []
    layout = ''
    metadata = new Map()
    is_new = false
    is_working_copy = false

    _modifications = new Set()

    constructor(classdata, rootstore) {
        makeObservable(this, {
            original: observable,
            name: observable,
            label: observable,
            fields: observable,
            layout: observable,
            metadata: observable,
            renamed_from: computed,
            is_working_copy: observable,
            is_new: observable,
            is_committed: computed,
            has_modified: computed,
            update: action.bound,
            setName: action.bound,
            setLabel: action.bound,
            setLayout: action.bound,
            setMetadataField: action.bound,
            clearMetadataField: action.bound,
            commitIfModified: action.bound,
            commit: action.bound,
            revert: action.bound,
            saveAsNew: action.bound,
            delete: action.bound,
            addField: action.bound,
            moveField: action.bound,
            removeField: action.bound,
            createLayoutComponent: action.bound,
            modifyLayoutComponent: action.bound,
            moveLayoutComponent: action.bound,
            deleteLayoutComponent: action.bound,
        })

        this._rootstore = rootstore
        this.update(classdata)
    }

    update = classdata => {
        updateInstanceWithData(this, classdata, [
            'gid',
            'original',
            'name',
            'label',
            'fields',
            'layout',
            'metadata',
            'is_new',
            'is_working_copy',
        ])
    }

    get renamed_from() {
        if (!this.is_working_copy) return null
        if (this.is_new) return null
        const original = this._rootstore.data.classes.get(this.original)
        if (!original || original.name === this.name) return null
        return original.name
    }

    get is_committed() {
        return !this.is_working_copy
    }

    get has_modified() {
        for (const fieldgid of this.fields) {
            const field = this._rootstore.data.fields.get(fieldgid)
            if (field && field.is_working_copy) {
                return true
            }
        }
        return false
    }

    setName = value => {
        const previous_value = this.name
        if (previous_value === value) return false
        if (test_empty(previous_value) && test_empty(value)) return false

        this.name = value
        this._modifications.add('name')
        return true
    }

    setLabel = (value, language) => {
        const previous_value = this.label.get(language)
        if (previous_value === value) return false
        if (test_empty(previous_value) && test_empty(value)) return false

        let classdata = { label: Object.fromEntries(this.label) }
        classdata['label'][language] = value
        this.update(classdata)
        this._modifications.add('label')
        return true
    }

    setLayout = gid => {
        const previous_gid = this.layout
        if (previous_gid === gid) return false
        if (test_empty(previous_gid) && test_empty(gid)) return false

        this.layout = gid
        this._modifications.add('layout')
        return true
    }

    setMetadataField = (name, type, is_localized, value, optional_language) => {
        const metadata = this['metadata']
        const languages = this._rootstore.project.languages
        const [new_metadata, is_updated] = setMetadataField(
            metadata,
            languages,
            name,
            type,
            is_localized,
            value,
            optional_language
        )
        if (!is_updated) {
            return false
        }
        this.update({ metadata: new_metadata })
        this._modifications.add('metadata')
        return true
    }

    clearMetadataField = name => {
        const metadata = this['metadata']
        const [new_metadata, is_updated] = clearMetadataField(metadata, name)
        if (!is_updated) {
            return false
        }
        this.update({ metadata: new_metadata })
        this._modifications.add('metadata')
        return true
    }

    commitIfModified = () => {
        // send gid + modified fields to server backend
        if (!this._modifications.size) {
            return Promise.resolve({ class: this.gid })
        }

        let updateparams = { class: this.gid }
        for (const fieldname of this._modifications.keys()) {
            updateparams[fieldname] = this[fieldname]
        }
        // basically fire & forget updates
        return this._rootstore._fetch('/classes/modify', updateparams).then(result => {
            this._modifications = new Set()
            return result
        })
    }

    commit = () => {
        const is_new = this.is_new
        return this._rootstore
            ._fetch('/classes/commit', { class: this.gid })
            .then(result => {
                if (!is_new) {
                    // delete the 'modified' version
                    this._rootstore.data.classes.delete(this.gid)
                    this._rootstore.data.layouts.delete(this.layout)
                }
                return result
            })
    }

    revert = () => {
        return this._rootstore
            ._fetch('/classes/revert', { class: this.gid })
            .then(result => {
                // delete the 'modified' version
                this._rootstore.data.classes.delete(this.gid)
                this._rootstore.data.layouts.delete(this.layout)
                return result
            })
    }

    saveAsNew = () => {
        return this._rootstore._fetch('/classes/save_as_new', {
            class: this.gid,
        })
    }

    copy = () => {
        return this._rootstore._fetch('/classes/copy', {
            class: this.gid,
        })
    }

    delete = () => {
        return this._rootstore
            ._fetch('/classes/delete', { class: this.gid })
            .then(result => {
                this._rootstore.data.classes.delete(this.gid)
                this._rootstore.data.layouts.delete(this.layout)
                return result
            })
            .catch(error => {
                // in use - report to user?
                return false
            })
    }

    addField = (field_gid, before_field_gid) => {
        let data = { class: this.gid, field: field_gid }
        if (before_field_gid) {
            data['before_field'] = before_field_gid
        }
        return this._rootstore._fetch('/classes/fields/add', data)
    }

    moveField = (field_gid, before_field_gid) => {
        let data = { class: this.gid, field: field_gid }
        if (before_field_gid) {
            data['before_field'] = before_field_gid
        }
        return this._rootstore._fetch('/classes/fields/move', data)
    }

    removeField = field_gid => {
        let data = { class: this.gid, field: field_gid }
        return this._rootstore._fetch('/classes/fields/remove', data)
    }

    createLayoutComponent = (type, parent_gid, before_component_gid, name, options) => {
        let data = { class: this.gid, parent: parent_gid, type: type }
        if (name) {
            data['name'] = name
        }
        if (options) {
            data['options'] = options
        }
        if (before_component_gid) {
            data['before_component'] = before_component_gid
        }
        return this._rootstore._fetch('/classes/layouts/components/create', data)
    }

    modifyLayoutComponent = (component_gid, updated_component) => {
        let data = { class: this.gid, component: component_gid }
        if (updated_component.type) {
            data['type'] = updated_component.type
        }
        if (updated_component.name) {
            data['name'] = updated_component.name
        }
        let options = {}
        for (let property_name in updated_component) {
            if (
                [
                    'type',
                    'name',
                    'gid',
                    'isContainer',
                    'isComponentContainer',
                    'parent',
                    'parentgid',
                ].includes(property_name)
            ) {
                continue
            }
            options[property_name] = updated_component[property_name]
        }
        if (options) {
            data['options'] = options
        }
        return this._rootstore._fetch('/classes/layouts/components/modify', data)
    }

    moveLayoutComponent = (component_gid, parent_gid, before_component_gid) => {
        let data = { class: this.gid, component: component_gid, parent: parent_gid }
        if (before_component_gid) {
            data['before_component'] = before_component_gid
        }
        return this._rootstore._fetch('/classes/layouts/components/move', data)
    }

    deleteLayoutComponent = component_gid => {
        let data = { class: this.gid, component: component_gid }
        return this._rootstore._fetch('/classes/layouts/components/delete', data)
    }
}
